/**
 * Storage Utility
 * Centralized storage management for cookies, localStorage, and sessionStorage
 * 
 * ARCHITECTURE:
 * - Cookies: Only for JWT token and remember-me flag (sent with requests)
 * - localStorage: User preferences, UI state, persistent auth data (remember me only)
 * - sessionStorage: Non-persistent auth data (cleared on browser close)
 * - Context/Memory: Runtime session data
 */

import { STORAGE_KEYS } from './constants';
import logger from './logger';

// ============================================================================
// STORAGE KEYS CONFIGURATION
// ============================================================================

/**
 * Keys that should be stored in localStorage (persistent)
 */
export const LOCAL_STORAGE_KEYS = {
  TOKEN: 'auth_token',
  USER_DATA: 'user_data',
  REMEMBER_ME: 'remember_me',
  THEME: 'theme_preference',
  SIDEBAR_STATE: 'sidebar_state',
  TABLE_SETTINGS: 'table_settings',
  ACTIVE_TABS: 'active_tabs',
};

/**
 * Keys that should remain in cookies (minimal, for HTTP requests)
 */
export const COOKIE_KEYS = {
  SESSION_ID: 'session_id',
  CSRF_TOKEN: 'csrf_token',
};

/**
 * DEPRECATED: Keys that should be removed (security risk)
 */
export const DEPRECATED_KEYS = [
  'KAPPU',      // Username stored in clear
  'KAPPP',      // Password stored in clear
  'KACHPPP',    // Current password stored in clear
  'KpppDecript', // Decrypted password
  'KuuuDecript', // Decrypted username
];

// ============================================================================
// LOCAL STORAGE UTILITIES
// ============================================================================

/**
 * Get item from localStorage with JSON parsing
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value if key doesn't exist
 * @returns {any} Parsed value or default
 */
export const getLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    return JSON.parse(item);
  } catch (error) {
    logger.error(`Error reading localStorage key "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Set item in localStorage with JSON stringification
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 */
export const setLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    logger.error(`Error writing localStorage key "${key}":`, error);
  }
};

/**
 * Remove item from localStorage
 * @param {string} key - Storage key
 */
export const removeLocalStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    logger.error(`Error removing localStorage key "${key}":`, error);
  }
};

/**
 * Clear all localStorage items (use with caution)
 */
export const clearLocalStorage = () => {
  try {
    localStorage.clear();
  } catch (error) {
    logger.error('Error clearing localStorage:', error);
  }
};

// ============================================================================
// AUTH STORAGE (Secure Implementation)
// ============================================================================

/**
 * User data structure for storage
 * @typedef {Object} StoredUserData
 * @property {number} id - User ID
 * @property {string} userName - Username
 * @property {string} employeeName - Display name
 * @property {string} email - Email address
 * @property {string} roleName - User roles
 * @property {number} roleID - Role ID
 * @property {number} companyID - Company ID
 * @property {string} departmentid - Department IDs
 * @property {string[]} privileges - User privileges
 * @property {number} idleTime - Idle timeout in minutes
 */

/**
 * Save authentication data securely
 * @param {string} token - JWT token
 * @param {StoredUserData} userData - User data
 * @param {boolean} rememberMe - Whether to persist session
 */
export const saveAuthData = (token, userData, rememberMe = false) => {
  const primaryStorage = rememberMe ? localStorage : sessionStorage;
  const secondaryStorage = rememberMe ? sessionStorage : localStorage;

  // Store token in selected storage
  primaryStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN, JSON.stringify(token));
  // Also store legacy token keys for backward compatibility
  primaryStorage.setItem('token', token);
  primaryStorage.setItem(STORAGE_KEYS.TOKEN, token);

  // Remove auth data from the non-selected storage to avoid stale sessions
  secondaryStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN);
  secondaryStorage.removeItem(LOCAL_STORAGE_KEYS.USER_DATA);
  secondaryStorage.removeItem(LOCAL_STORAGE_KEYS.REMEMBER_ME);
  secondaryStorage.removeItem(STORAGE_KEYS.TOKEN);
  secondaryStorage.removeItem('token');

  // Store user data
  const sanitizedUserData = {
    id: userData.id,
    userName: userData.userName,
    employeeName: userData.employeeName,
    email: userData.email,
    roleName: userData.roleName,
    roleID: userData.roleID,
    companyID: userData.companyID,
    departmentid: userData.departmentid,
    privileges: userData.privileges || [],
    locationStatus: userData.locationStatus,
    idleTime: userData.idleTime, // Include idle time
  };
  
  primaryStorage.setItem(LOCAL_STORAGE_KEYS.USER_DATA, JSON.stringify(sanitizedUserData));
  primaryStorage.setItem(LOCAL_STORAGE_KEYS.REMEMBER_ME, JSON.stringify(rememberMe));
  
  logger.info('Auth data saved securely');
};

/**
 * Get stored authentication data
 * @returns {{ token: string|null, userData: StoredUserData|null, rememberMe: boolean }}
 */
export const getAuthData = () => {
  const parseStoredValue = (storage, key, defaultValue = null) => {
    try {
      const item = storage.getItem(key);
      if (item === null) return defaultValue;
      return JSON.parse(item);
    } catch {
      return defaultValue;
    }
  };

  const sessionToken = parseStoredValue(sessionStorage, LOCAL_STORAGE_KEYS.TOKEN);
  const localToken = parseStoredValue(localStorage, LOCAL_STORAGE_KEYS.TOKEN);
  const token = sessionToken || localToken;

  const userData = sessionToken
    ? parseStoredValue(sessionStorage, LOCAL_STORAGE_KEYS.USER_DATA)
    : parseStoredValue(localStorage, LOCAL_STORAGE_KEYS.USER_DATA);

  const rememberMe = sessionToken
    ? false
    : parseStoredValue(localStorage, LOCAL_STORAGE_KEYS.REMEMBER_ME, false);

  return {
    token,
    userData,
    rememberMe: !!rememberMe,
  };
};

/**
 * Clear all authentication data (logout)
 */
export const clearAuthData = () => {
  removeLocalStorage(LOCAL_STORAGE_KEYS.TOKEN);
  removeLocalStorage(LOCAL_STORAGE_KEYS.USER_DATA);
  removeLocalStorage(LOCAL_STORAGE_KEYS.REMEMBER_ME);

  sessionStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN);
  sessionStorage.removeItem(LOCAL_STORAGE_KEYS.USER_DATA);
  sessionStorage.removeItem(LOCAL_STORAGE_KEYS.REMEMBER_ME);

  // Also clear legacy token keys
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
  localStorage.removeItem('token');
  sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
  sessionStorage.removeItem('token');
  
  logger.info('Auth data cleared');
};

/**
 * Check if user is authenticated (has valid token)
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  const localToken = getLocalStorage(LOCAL_STORAGE_KEYS.TOKEN);
  const sessionToken = (() => {
    try {
      const item = sessionStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  })();
  return !!(sessionToken || localToken);
};

// ============================================================================
// UI PREFERENCES STORAGE
// ============================================================================

/**
 * Save UI preference
 * @param {string} key - Preference key
 * @param {any} value - Preference value
 */
export const saveUIPreference = (key, value) => {
  const preferences = getLocalStorage('ui_preferences', {});
  preferences[key] = value;
  setLocalStorage('ui_preferences', preferences);
};

/**
 * Get UI preference
 * @param {string} key - Preference key
 * @param {any} defaultValue - Default value
 * @returns {any}
 */
export const getUIPreference = (key, defaultValue = null) => {
  const preferences = getLocalStorage('ui_preferences', {});
  return preferences[key] !== undefined ? preferences[key] : defaultValue;
};

/**
 * Save active tab state
 * @param {string} page - Page identifier
 * @param {string|number} tabId - Active tab identifier
 */
export const saveActiveTab = (page, tabId) => {
  const tabs = getLocalStorage(LOCAL_STORAGE_KEYS.ACTIVE_TABS, {});
  tabs[page] = tabId;
  setLocalStorage(LOCAL_STORAGE_KEYS.ACTIVE_TABS, tabs);
};

/**
 * Get active tab state
 * @param {string} page - Page identifier
 * @param {string|number} defaultTab - Default tab
 * @returns {string|number}
 */
export const getActiveTab = (page, defaultTab = 0) => {
  const tabs = getLocalStorage(LOCAL_STORAGE_KEYS.ACTIVE_TABS, {});
  return tabs[page] !== undefined ? tabs[page] : defaultTab;
};

/**
 * Save table settings (pagination, sorting, etc.)
 * @param {string} tableId - Table identifier
 * @param {Object} settings - Table settings
 */
export const saveTableSettings = (tableId, settings) => {
  const allSettings = getLocalStorage(LOCAL_STORAGE_KEYS.TABLE_SETTINGS, {});
  allSettings[tableId] = settings;
  setLocalStorage(LOCAL_STORAGE_KEYS.TABLE_SETTINGS, allSettings);
};

/**
 * Get table settings
 * @param {string} tableId - Table identifier
 * @returns {Object|null}
 */
export const getTableSettings = (tableId) => {
  const allSettings = getLocalStorage(LOCAL_STORAGE_KEYS.TABLE_SETTINGS, {});
  return allSettings[tableId] || null;
};

// ============================================================================
// MIGRATION UTILITIES
// ============================================================================

/**
 * Migrate data from old cookie-based storage to new localStorage-based storage
 * @param {Object} cookies - Current cookies object
 */
export const migrateFromCookies = (cookies) => {
  // Only migrate if no auth data exists in new storage
  if (isAuthenticated()) {
    logger.info('Auth data already exists, skipping migration');
    return;
  }
  
  // Check if old cookie data exists
  if (cookies.userID && cookies.token) {
    logger.info('Migrating auth data from cookies to localStorage');
    
    const userData = {
      id: cookies.userID,
      userName: cookies.userName,
      employeeName: cookies.employeeName,
      email: cookies.emailID,
      roleName: cookies.roleName,
      roleID: cookies.roleID,
      companyID: cookies.companyID,
      departmentid: cookies.departmentid,
      privileges: cookies.UserPrivileges || cookies.userPrivileges || [],
      locationStatus: cookies.locationStatus,
    };
    
    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || cookies.token;
    const rememberMe = cookies.Rememberme === true || cookies.Rememberme === 'true';
    
    saveAuthData(token, userData, rememberMe);
  }
};

/**
 * Get list of cookies that should be removed after migration
 * @returns {string[]}
 */
export const getCookiesToRemove = () => {
  return [
    // Security risk - MUST be removed
    ...DEPRECATED_KEYS,
    // Auth data - moved to localStorage
    'userID',
    'userName', 
    'employeeName',
    'emailID',
    'roleName',
    'roleID',
    'companyID',
    'departmentid',
    'UserPrivileges',
    'userPrivileges',
    'locationStatus',
    'idleTime',
    'AssociateTitle',
    'ProfilePicture',
    'roleCount',
    'FirstroleCountLogin',
    // UI state - moved to localStorage
    'EmployeeTableCookies',
    'ProfileTableCookies',
    'DashboardActiveTabCookies',
    'ApplicationConfigActiveTab',
    'ISReloadfunction',
    'SidebarReload',
    // Legacy
    'CT_HRMS_APP',
    'token',
  ];
};

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

const storage = {
  // LocalStorage
  get: getLocalStorage,
  set: setLocalStorage,
  remove: removeLocalStorage,
  clear: clearLocalStorage,
  
  // Auth
  saveAuthData,
  getAuthData,
  clearAuthData,
  isAuthenticated,
  
  // UI Preferences
  saveUIPreference,
  getUIPreference,
  saveActiveTab,
  getActiveTab,
  saveTableSettings,
  getTableSettings,
  
  // Migration
  migrateFromCookies,
  getCookiesToRemove,
  
  // Constants
  LOCAL_STORAGE_KEYS,
  COOKIE_KEYS,
  DEPRECATED_KEYS,
};

export default storage;

